/**
 * TicketContextPanel — the ownership chain behind a service call.
 *
 * A site is rarely just a site. It belongs to a franchise, which belongs to a
 * corporation, which has an owner, and the site itself has the people you
 * actually phone when you're standing at a locked door. None of that was
 * surfaced on the ticket. It is now.
 *
 * Also renders the ENTIRE service address, not just the location name.
 */
import { useEffect, useState } from 'react';
import {
  Box, Typography, Chip, Divider, Link, Stack, Skeleton, Tooltip, IconButton,
} from '@mui/material';
import {
  Business as CorpIcon, Storefront as FranchiseIcon, Person as OwnerIcon,
  Place as PlaceIcon, Phone as PhoneIcon, Email as EmailIcon,
  ContentCopy as CopyIcon, Map as MapIcon,
} from '@mui/icons-material';
import { ticketOpsApi } from '../../services/api';

const LBL = { fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: '#94a3b8', textTransform: 'uppercase' as const };

function Row({ icon, label, value, sub }: any) {
  if (!value) return null;
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
      <Box sx={{ color: '#94a3b8', mt: '2px' }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={LBL}>{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </Box>
    </Stack>
  );
}

export default function TicketContextPanel({ ticketId }: { ticketId: number }) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ticketId) return;
    setLoading(true);
    ticketOpsApi.context(ticketId)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [ticketId]);

  if (loading) return <Box sx={{ py: 1 }}><Skeleton height={28} /><Skeleton height={28} /><Skeleton height={28} /></Box>;
  if (!data) return null;

  const addr = data.service_address || {};
  const contacts = data.contacts || [];
  const copy = (t: string) => { try { navigator.clipboard.writeText(t); } catch { /* no clipboard */ } };
  const mapsHref = addr.single_line
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr.single_line)}`
    : null;

  return (
    <Box sx={{ mt: 1 }}>
      {/* ── OWNERSHIP CHAIN ─────────────────────────────────────────── */}
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        {data.corporation?.name && (
          <Chip size="small" icon={<CorpIcon sx={{ fontSize: 15 }} />} label={`Corp: ${data.corporation.name}`}
                sx={{ bgcolor: '#eef2ff', color: '#4338ca', fontWeight: 600 }} />
        )}
        {data.franchise?.name && (
          <Chip size="small" icon={<FranchiseIcon sx={{ fontSize: 15 }} />}
                label={`Franchise: ${data.franchise.name}${data.franchise.number ? ` (#${data.franchise.number})` : ''}`}
                sx={{ bgcolor: '#ecfeff', color: '#0e7490', fontWeight: 600 }} />
        )}
        <Chip size="small" icon={<PlaceIcon sx={{ fontSize: 15 }} />}
              label={`Site: ${data.site?.company || data.site?.name || '—'}`}
              sx={{ bgcolor: '#f1f5f9', color: '#334155', fontWeight: 600 }} />
      </Stack>

      {!data.corporation?.name && !data.franchise?.name && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          No franchise or corporation recorded for this site yet.
        </Typography>
      )}

      <Row icon={<OwnerIcon sx={{ fontSize: 17 }} />} label="Owner"
           value={data.owner?.name}
           sub={[data.owner?.phone, data.owner?.email].filter(Boolean).join('  ·  ')} />

      <Divider sx={{ my: 1.5 }} />

      {/* ── FULL SERVICE ADDRESS ────────────────────────────────────── */}
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ color: '#94a3b8', mt: '2px' }}><PlaceIcon sx={{ fontSize: 17 }} /></Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={LBL}>
            Service address {addr.source === 'customer' ? '(from the customer record)' : ''}
          </Typography>
          {(addr.multi_line || []).length ? (
            (addr.multi_line || []).map((l: string, i: number) => (
              <Typography key={i} variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>{l}</Typography>
            ))
          ) : (
            <Typography variant="body2" color="error">No service address on this ticket.</Typography>
          )}
          {addr.single_line && (
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
              <Tooltip title="Copy address">
                <IconButton size="small" onClick={() => copy(addr.single_line)}><CopyIcon sx={{ fontSize: 15 }} /></IconButton>
              </Tooltip>
              {mapsHref && (
                <Tooltip title="Open in maps">
                  <IconButton size="small" component="a" href={mapsHref} target="_blank" rel="noopener noreferrer">
                    <MapIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          )}
        </Box>
      </Stack>

      <Divider sx={{ my: 1.5 }} />

      {/* ── CONTACTS LINKED TO THE SITE ─────────────────────────────── */}
      <Typography sx={{ ...LBL, mb: 0.75 }}>Contacts at this site ({contacts.length})</Typography>
      {contacts.length === 0 && (
        <Typography variant="caption" color="text.secondary">No contacts linked to this site.</Typography>
      )}
      <Stack spacing={1}>
        {contacts.map((c: any, i: number) => (
          <Box key={c.id ?? `t${i}`}
               sx={{ p: 1, border: '1px solid #e2e8f0', borderRadius: 1,
                     bgcolor: c.contact_role === 'ticket' ? '#fffbeb' : '#fff' }}>
            <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{c.name || '—'}</Typography>
              {c.is_primary ? <Chip size="small" label="Primary" sx={{ height: 17, fontSize: 9, fontWeight: 700 }} /> : null}
              {c.contact_role && c.contact_role !== 'site' && (
                <Chip size="small" label={c.contact_role} variant="outlined" sx={{ height: 17, fontSize: 9 }} />
              )}
            </Stack>
            {c.title && <Typography variant="caption" color="text.secondary">{c.title}</Typography>}
            <Stack direction="row" spacing={1.5} sx={{ mt: 0.25 }} flexWrap="wrap" useFlexGap>
              {(c.phone || c.mobile) && (
                <Link href={`tel:${c.phone || c.mobile}`} underline="hover"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.4, fontSize: 12 }}>
                  <PhoneIcon sx={{ fontSize: 13 }} />{c.phone || c.mobile}
                </Link>
              )}
              {c.email && (
                <Link href={`mailto:${c.email}`} underline="hover"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.4, fontSize: 12, wordBreak: 'break-all' }}>
                  <EmailIcon sx={{ fontSize: 13 }} />{c.email}
                </Link>
              )}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
